import {
  Box,
  FormHelperText,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { CollapseFilter } from '../collapse-filter';

type TextRangeValue = {
  from: string | null;
  to: string | null;
};

type TextRangeFilterProps = {
  title: string;
  value: TextRangeValue;
  onChange: (value: TextRangeValue) => void;
  fromPlaceholder?: string;
  toPlaceholder?: string;
  endAdornment?: string;
  numeric?: boolean;
  maxLength?: number;
  disabled?: boolean;
  helperText?: string;
  onError?: (isError: boolean) => void;
};

export const TextRangeFilter = (props: TextRangeFilterProps) => {
  const {
    title,
    value,
    onChange,
    fromPlaceholder = 'Від',
    toPlaceholder = 'До',
    endAdornment,
    numeric,
    maxLength,
    helperText,
    disabled,
    onError,
  } = props;

  const isInvalidRange = (range: TextRangeValue) => {
    if (range.from === null || range.to === null) return false;
    return numeric
      ? Number(range.to) < Number(range.from)
      : range.to.localeCompare(range.from) < 0;
  };

  const invalid = isInvalidRange(value);

  const handleChange =
    (field: 'from' | 'to') => (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      const nextValue = (numeric ? raw.replace(/\D/g, '') : raw) || null;
      const next: TextRangeValue = { ...value, [field]: nextValue };

      onChange(next);
      onError?.(isInvalidRange(next));
    };

  const slotProps = {
    input: endAdornment
      ? { endAdornment: <InputAdornment position="end">{endAdornment}</InputAdornment> }
      : undefined,
    htmlInput:
      numeric || maxLength
        ? { inputMode: numeric ? ('numeric' as const) : undefined, maxLength }
        : undefined,
  };

  const displayHelperText = invalid
    ? 'Значення "До" не може бути меншим за "Від"'
    : helperText;

  return (
    <CollapseFilter title={title}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            placeholder={fromPlaceholder}
            size="small"
            fullWidth
            variant="outlined"
            value={value.from ?? ''}
            error={invalid}
            onChange={handleChange('from')}
            slotProps={slotProps}
            disabled={disabled}
          />
          <Typography>-</Typography>
          <TextField
            placeholder={toPlaceholder}
            size="small"
            fullWidth
            variant="outlined"
            value={value.to ?? ''}
            error={invalid}
            onChange={handleChange('to')}
            slotProps={slotProps}
            disabled={disabled}
          />
        </Stack>
        {displayHelperText && (
          <FormHelperText error={invalid}>{displayHelperText}</FormHelperText>
        )}
      </Box>
    </CollapseFilter>
  );
};
